// ============================================
// Auth - Firebase Module (ES6)
// Firebase 초기화
// ============================================

import { showToast, hideGlobalLoading } from '../utils.js';
import { setAuth, setStorage, setCurrentUser } from './state.js';
import { closeLoginModal, closeEmailLoginModal, showEmailVerificationModal } from './login.js';
import { hideSplashScreen, showApp } from '../app/display.js';
import { loadUserData, checkAdminStatus } from './user.js';
import { loadModelOptionsFromServer } from '../config.js';

// ============================================
// Firebase Initialization
// ============================================

export async function initializeFirebase() {
  try {
    firebase.initializeApp(window.firebaseConfig);
    const auth = firebase.auth();
    const storage = firebase.storage();

    setAuth(auth);
    setStorage(storage);

    // 모델 옵션을 서버에서 로드
    loadModelOptionsFromServer().catch(e => {
      console.warn('Failed to load model options:', e);
    });

    // Firestore 설정 (WebView 호환성)
    firebase.firestore().settings({
      experimentalForceLongPolling: true,
      experimentalAutoDetectLongPolling: false
    });

    // 연결 복구 시도 함수
    window.retryFirestoreConnection = async () => {
      try {
        showToast('연결이 복구되었습니다');
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          if (typeof loadChatList === 'function') loadChatList();
          loadUserData();
        }
        if (typeof loadHomeContent === 'function') loadHomeContent();
      } catch (e) {
        console.error('Failed to reload data:', e);
      }
    };

    // Auth persistence 설정
    try {
      await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      console.log('Auth persistence: LOCAL');
    } catch (persistError) {
      console.warn('LOCAL persistence failed, trying SESSION:', persistError.message);
      try {
        await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
        console.log('Auth persistence: SESSION');
      } catch (sessionError) {
        console.warn('SESSION persistence failed, using NONE:', sessionError.message);
      }
    }

    // Capacitor redirect 로그인 결과 처리
    try {
      const redirectResult = await auth.getRedirectResult();
      if (redirectResult?.user) {
        console.log('Redirect 로그인 성공:', redirectResult.user.email);
        if (redirectResult.additionalUserInfo?.isNewUser) {
          const { FUNCTIONS_BASE } = await import('../config.js');
          try {
            const idToken = await redirectResult.user.getIdToken();
            await fetch(`${FUNCTIONS_BASE}/createUser`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify({ nickname: '' })
            });
          } catch (createError) {
            console.warn('createUser failed:', createError);
          }
        }
      }
    } catch (redirectError) {
      console.warn('getRedirectResult error:', redirectError);
    }

    // Auth state listener
    let isAppInitialized = false;
    auth.onAuthStateChanged(async (user) => {
      console.log('Auth state changed:', user ? user.email : 'logged out');
      if (user) {
        const currentUser = firebase.auth().currentUser;
        if (currentUser && currentUser.uid === user.uid && isAppInitialized) {
          console.log('Same user, skipping re-init');
          return;
        }

        // Google 로그인 여부 확인 (Google은 항상 emailVerified=true)
        const isGoogleUser = user.providerData?.some(
          p => p.providerId === 'google.com'
        );

        // 이메일 로그인이면서 미인증인 경우 인증 모달 표시
        if (!isGoogleUser && !user.emailVerified) {
          console.log('Email not verified, showing verification modal');
          setCurrentUser(user);
          hideSplashScreen();
          hideGlobalLoading();
          showEmailVerificationModal(user.email);
          return;
        }

        setCurrentUser(user);
        window._isIntentionalLogout = false;
        try {
          await showApp(true);
          isAppInitialized = true;
          hideGlobalLoading();

          // 어드민 상태 확인 (네비게이션 가시성 업데이트)
          checkAdminStatus();

          // 화면 전환 완료 후 모달 닫기
          closeLoginModal();
          closeEmailLoginModal();
        } catch (e) {
          console.error('showApp error:', e);
          hideGlobalLoading();
        }
      } else {
        setCurrentUser(null);
        isAppInitialized = false;
        await showApp(false);
      }
    });

    console.log('Firebase 초기화 완료!');
  } catch (e) {
    console.error('Firebase 초기화 오류:', e);
    showToast('Firebase 연결 오류: ' + e.message, true);
  }
}
